import { HeavyDutyMachineryNotificationType } from '../interfaces/heavyDutyMachinery/INotification';
import { TRANSLATIONS } from './translations';

/**
 * The headline for every kind of notification this app can raise, by type.
 *
 * It lives here rather than inside the bell because it now has two readers, and
 * the second one is not a React component: the push service worker composes the
 * title of a notification arriving on a locked phone from this same map (see
 * PushNotificationService.syncPushCopy), so the words a person reads in the app
 * and the words they read on their lock screen cannot drift apart.
 *
 * Exhaustive by type, and TypeScript keeps it that way -- a notification type
 * added without a title here does not compile, which is the whole reason this
 * is a Record and not a lookup with a default.
 */
export const NOTIF_TITLE_KEYS: Record<HeavyDutyMachineryNotificationType, string> = {
  NewBooking: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_NEW_BOOKING,
  NewMessage: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_NEW_MESSAGE,
  BookingStatusChanged: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_BOOKING_STATUS_CHANGED,
  AuditPassed: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_AUDIT_PASSED,
  AuditFailed: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_AUDIT_FAILED,
  AdminAnnouncement: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_ANNOUNCEMENT,
  PayoutsDisabled: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_PAYOUTS_DISABLED,
  PayoutsRestored: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_PAYOUTS_RESTORED,
  BookingConfirmed: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_BOOKING_CONFIRMED,
  BookingPaymentFailed: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_BOOKING_PAYMENT_FAILED,
  SpaceApproved: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_SPACE_APPROVED,
  SpaceRejected: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_SPACE_REJECTED,
  AdminMessage: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_ADMIN_MESSAGE,
  GeofenceAlert: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_GEOFENCE_ALERT,
  NewServiceRequest: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_NEW_SERVICE_REQUEST,
  ServiceRequestQuoted: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_SERVICE_REQUEST_QUOTED,
  ServiceRequestAccepted: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_SERVICE_REQUEST_ACCEPTED,
  ServiceRequestDeclined: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_SERVICE_REQUEST_DECLINED,
  ServiceRequestCancelled: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_SERVICE_REQUEST_CANCELLED,
  ServiceRequestExpired: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_SERVICE_REQUEST_EXPIRED,
  ServiceVisitScheduled: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_SERVICE_VISIT_SCHEDULED,
  ServiceVisitNotConfirmed: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_SERVICE_VISIT_NOT_CONFIRMED,
  ServiceVisitConfirmationMissed: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_SERVICE_VISIT_MISSED,
  ServiceCompleted: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_SERVICE_COMPLETED,
  GeofenceResolved: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_GEOFENCE_RESOLVED,
  GeofenceZoneSet: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_GEOFENCE_ZONE_SET,
  GeofenceMoveRequested: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_GEOFENCE_MOVE_REQUESTED,
  GeofenceMoveApproved: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_GEOFENCE_MOVE_APPROVED,
  GeofenceMoveRejected: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_GEOFENCE_MOVE_REJECTED,
  OvertimeChargeFailed: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_OVERTIME_CHARGE_FAILED,
  NewQuoteRequest: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_NEW_QUOTE_REQUEST,
  QuoteAccepted: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_QUOTE_ACCEPTED,
  QuoteRevised: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_QUOTE_REVISED,
  TransportStarted: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_TRANSPORT_STARTED,
  OffRentDeclared: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_OFF_RENT_DECLARED,
  CollectionStarted: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_COLLECTION_STARTED,
  FundsReleased: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_FUNDS_RELEASED,
  TransferPendingResolution: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_TRANSFER_PENDING,
  BookingRefunded: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_BOOKING_REFUNDED,
  HostDepositFunded: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_HOST_DEPOSIT_FUNDED,
  HostDepositCharged: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_HOST_DEPOSIT_CHARGED,
  BookingFundsHeld: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_FUNDS_HELD,
  BookingAuthorizationFailed: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_AUTH_FAILED,
  BookingCharged: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_BOOKING_CHARGED,
  DisputeClosedByDeadline: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_DISPUTE_CLOSED_BY_DEADLINE,
  OperatorSuspended: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_OPERATOR_SUSPENDED,
  OperatorReinstated: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_OPERATOR_REINSTATED,
  ServiceProviderSuspended: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_PROVIDER_SUSPENDED,
  ServiceProviderReinstated: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_PROVIDER_REINSTATED,
  HandoverAcknowledged: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_HANDOVER_ACKNOWLEDGED,
  HandoverRejected: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_HANDOVER_REJECTED,
  DepositAuthorizationFailed: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_DEPOSIT_AUTH_FAILED,
  DisputeOpened: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_DISPUTE_OPENED,
  DisputeAnswered: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_DISPUTE_ANSWERED,
  DisputeOffer: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_DISPUTE_OFFER,
  DisputeSettled: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_DISPUTE_SETTLED,
  DisputeSplit: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_DISPUTE_SPLIT,
  DisputeWithdrawn: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_DISPUTE_WITHDRAWN,
  DisputeResolved: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_DISPUTE_RESOLVED,
};
